// Modo SIMULADOR — injeta dados FAKE (client-side, temporarios) nas telas
// pra visualizar/formatar o layout quando a maquina ainda nao envia dados
// (ex.: bluebox sem ingestao). Nao persiste nada no backend; e so display.
//
// Gate: exclusivo do desenvolvedor (Jonathan Oliveira). Nunca aparece pra
// operador/cliente. Ver isDeveloper abaixo.

const normalize = (v) =>
  (v || '')
    .toString()
    .trim()
    .toLowerCase();

// Quem pode ver/usar o simulador. Casa por email (prefixo) OU por partes
// do nome. Lista restrita de proposito — o botao so aparece pra essas
// pessoas (dev + quem esta validando o layout).
const SIMULATOR_ALLOWED = [
  { email: 'jonathan.oliveira', nameParts: ['jonathan', 'oliveira'] },
  { email: 'daniela.losekan', nameParts: ['daniela', 'losekan'] },
];

export const isDeveloper = (user) => {
  if (!user) return false;
  const email = normalize(user.email);
  const name = normalize(user.name);
  return SIMULATOR_ALLOWED.some(
    (allowed) =>
      (allowed.email && email.includes(allowed.email)) ||
      allowed.nameParts.every((part) => name.includes(part)),
  );
};

// Angulos de inclinacao pra CICLAR no simulador — a cada ativacao mostra
// um proximo, pra testar como a imagem fica em cada posicao (inclui 45/90).
export const SIMULATOR_TILT_ANGLES = [
  2.5,
  10,
  20,
  45,
  90,
  135,
  180,
  -45,
  -90,
];

// Pega o angulo da vez pelo indice do ciclo (wrap circular, aceita negativo).
export const simulatedTilt = (n) => {
  const list = SIMULATOR_TILT_ANGLES;
  const i = (((Number(n) || 0) % list.length) + list.length) % list.length;
  return list[i];
};

// Dados simulados de UMA thing. DETERMINISTICO (varia so por index) pra o
// memo do consumidor ficar estavel e os valores nao "dancarem" a cada
// render. Inclui campos da BLUEBOX (codigos curtos) E os padrao (bomba/
// UCHA) — cada tela le os que precisa.
export const buildSimulatedThingData = (index = 0) => {
  const k = Number(index) || 0;
  const round = (n, d = 2) => Number(n.toFixed(d));
  return {
    // --- BLUEBOX (codigos curtos MQTT) ---
    W: 52000 + k * 3000, // potencia ativa total (W) -> ~52 kW
    WR: round((52000 + k * 3000) / 3),
    WS: round((52000 + k * 3000) / 3),
    WT: round((52000 + k * 3000) / 3),
    VAR: 12000 + k * 500,
    VA: 55000 + k * 3000,
    Iavg: round(92 + k * 2, 1),
    IR: round(91 + k * 2, 1),
    IS: round(92 + k * 2, 1),
    IT: round(93 + k * 2, 1),
    VFavg: 254,
    VFR: 253,
    VFS: 254,
    VFT: 255,
    VLavg: 440,
    VLRS: 439,
    VLST: 440,
    VLTR: 441,
    FP: round(0.88 - k * 0.01, 3),
    FPR: 0.87,
    FPS: 0.88,
    FPT: 0.89,
    Freq: 60,
    Rev: 1775 + k * 5,
    Press: round(4.2 + k * 0.15, 2),
    Batt: Math.max(5, 92 - k * 4),
    Tilt: round(2.5 + k * 0.6, 1),
    TmpR: round(66 + k, 1),
    TmpS: round(68 + k, 1),
    TmpT: round(67 + k, 1),
    TmpRa: round(54 + k, 1),
    TmpAx: round(51 + k, 1),
    TmpCPU: round(42 + k, 1),
    Lvl1: 1,
    Lvl2: 0,
    Hor: 12450 + k * 120,
    ActE: 1284500 + k * 10000, // energia ativa direta (kWh)
    RevE: 320 + k * 10,
    THDVR: 2.1,
    THDVS: 2.3,
    THDVT: 2.0,
    THDIR: 3.4,
    THDIS: 3.1,
    THDIT: 3.6,
    Vsns: 24,
    Vsup: 220,
    Vcpu: 3.3,
    SnrSup: 42,
    SnrGat: 38,
    SpvOk: 1,
    MgeOk: 1,
    Alr1: 0,
    Alr2: 0,
    Trp1: 0,
    Trp2: 0,
    Lat: -29.68,
    Lon: -53.8,
    // --- Padrao (bomba/UCHA) ---
    PotenciaAtivaMedia: round(52 + k * 3, 2), // kW
    Vazao: round(145 + k * 10, 2), // l/s
    PressaoAlimentacao: round(3.1 + k * 0.1, 2),
    PressaoDescarga: round(5.4 + k * 0.1, 2),
    PressaoRecalque: round(5.4 + k * 0.1, 2),
    EnergiaAtiva: 1200000 + k * 10000,
    EnergiaAtivaReversa: 5000 + k * 100,
    EnergiaConsumidaTotal: 1284500 + k * 10000,
  };
};

// Serie historica FAKE pra o grafico (LineChart) quando nao ha dado real.
// Gera N pontos terminando "agora", espacados conforme o periodo, com uma
// forma suave (senoides) em torno de um valor base derivado do range da
// variavel (ou de um hash do ihmname). Ponto no mesmo shape do historico
// real: { timestamp (segundos), value }.
export const buildSimulatedSeries = (selectedVar, period, nowMs) => {
  const now = Number(nowMs) || Date.now();
  const cfg =
    period === 'monthly'
      ? { n: 180, stepMs: 4 * 3600 * 1000 }
      : period === 'weekly'
      ? { n: 168, stepMs: 3600 * 1000 }
      : { n: 144, stepMs: 10 * 60 * 1000 };

  // Valor base: 60% do maior limite de range da variavel; senao hash do
  // ihmname pra variaveis diferentes terem patamares diferentes.
  let base = 50;
  const maxes = ((selectedVar && selectedVar.range) || [])
    .map((r) => Number(r && r.max))
    .filter((n) => Number.isFinite(n));
  if (maxes.length) {
    base = Math.max(...maxes) * 0.6 || 50;
  } else {
    const name = (selectedVar && selectedVar.ihmname) || 'x';
    let h = 0;
    for (let i = 0; i < name.length; i += 1) {
      h = (h * 31 + name.charCodeAt(i)) % 1000;
    }
    base = 20 + (h % 100);
  }
  const amp = Math.abs(base) * 0.15 || 5;

  const series = [];
  for (let i = 0; i < cfg.n; i += 1) {
    const tsMs = now - (cfg.n - 1 - i) * cfg.stepMs;
    const phase = (i / cfg.n) * Math.PI * 6;
    const wobble = Math.sin(phase) * amp + Math.sin(phase * 2.3) * amp * 0.3;
    const value = Math.max(0, base + wobble);
    series.push({
      timestamp: Math.floor(tsMs / 1000),
      value: Number(value.toFixed(2)),
    });
  }
  return series;
};

// Aplica o simulador a um array de dados de things. Desligado: retorna o
// array cru intacto (comportamento normal). Ligado: pra cada thing devolve
// os dados simulados COM os reais sobrepostos (dado real ganha; campos
// ausentes/maquina sem dado ficam com o fake). Sempre alinhado a `things`
// (mesmo indice do carrossel), entao funciona ate quando o fetch real
// falhou e o array veio vazio.
export const applySimulator = (dataArray, things, enabled) => {
  if (!enabled) return dataArray;
  const list = Array.isArray(things) ? things : [];
  return list.map((_, index) => ({
    ...buildSimulatedThingData(index),
    ...((dataArray && dataArray[index]) || {}),
  }));
};
